import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ArrowLeft, Calendar, ArrowRight, Camera, ListChecks, Images, Edit } from 'lucide-react';
import type { CurrentPage } from '../../App';
import PhotoCapture from '../PhotoCapture';
import PhotoGalleryModal from '../PhotoGalleryModal';
import { PhotoData } from '../../services/CameraService';
import { RetrofitSurveyData, getRetrofitSurveyAddress } from '../../utils/retrofitSurveyStorage';
import { usePhotoHandling } from '../../hooks/usePhotoHandling';
import { useRetrofitPhotoUpload } from '../../hooks/useRetrofitPhotoUpload';
import type { RoomPhoto } from '../../types';
import type { FieldSchema } from '../../types/formSchema';
import SignatureCanvas from 'react-signature-canvas';
import SignatureModal from '../SignatureModal';

interface RetrofitSurveyProps {
  onNavigate: (page: CurrentPage) => void;
  onNavigateNext: () => void;
  floorPlanId?: string;
  surveyData?: RetrofitSurveyData;
  surveyId?: string;
  onDataUpdate?: (updater: (prev: RetrofitSurveyData) => RetrofitSurveyData, sectionId?: string) => void;
  onBatchUploadStart?: () => void;
  onBatchUploadEnd?: () => void;
  onManualSave?: () => void; // Trigger manual save after photo capture
  onSaveImmediately?: () => Promise<string>; // Trigger immediate save on blur/unmount
  onGalleryStateChange?: (isOpen: boolean) => void; // Notify when gallery opens/closes
  hiddenFields?: string[]; // List of field IDs to hide
  fieldOrder?: string[]; // Custom field order
  customFields?: FieldSchema[]; // Custom fields for this section
  hiddenSections?: string[]; // List of custom field section names to hide
  sectionOrder?: string[]; // Order of section IDs (built-in sections)
}

// Photo field type for RetrofitSurvey photos
interface PhotoField {
  id: string;
  fileName?: string;
  url?: string;
  localPath?: string;
  uploading?: boolean;
  error?: string;
  note?: string;
  caption?: string;
  timestamp?: string;
  order?: number;
}

interface PhotoSection {
  photos: PhotoField[];
}

const RetrofitSurvey: React.FC<RetrofitSurveyProps> = ({ 
  onNavigate, 
  onNavigateNext, 
  floorPlanId, 
  surveyData,
  surveyId, 
  onDataUpdate,
  hiddenFields = [],
  hiddenSections = [],
  fieldOrder,
  sectionOrder,
  onBatchUploadStart,
  onBatchUploadEnd,
  onManualSave,
  onSaveImmediately,
  onGalleryStateChange,
  customFields = []
}) => {
  const isRestoringFromProps = useRef(false);
  const lastRestoredSurveyId = useRef<string | null>(null);
  
  // Signature state and refs for custom signature fields
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [currentSignatureField, setCurrentSignatureField] = useState<string>('');  
  const signatureRefs = useRef<Record<string, React.RefObject<SignatureCanvas>>>({});
  
  // Create refs for signature fields
  useEffect(() => {
    customFields.forEach(field => {
      if (field.type === 'signature' && !signatureRefs.current[field.id]) {
        signatureRefs.current[field.id] = React.createRef<SignatureCanvas>();
      }
    });
  }, [customFields]);
  
  // Helper to check if a field is hidden
  const isFieldHidden = (fieldId: string) => hiddenFields.includes(fieldId);
  
  // Define field sections for organized display (default order)
  const defaultFieldSections = [
    {
      id: 'survey_info',
      title: 'Survey Information',
      fields: ['surveyDate', 'surveyorName']
    },
    {
      id: 'customer_details',
      title: 'Customer Details',
      fields: ['customerFullName', 'customerEmail', 'customerMobile']
    },
    {
      id: 'property_address',
      title: 'Property Address',
      fields: ['buildingName', 'houseNumberName', 'town', 'postCode']
    },
    {
      id: 'property_details',
      title: 'Property Details',
      fields: ['tenure', 'buildingAge', 'dwellingType', 'buildingOrientation', 'majorityWallType']
    },
    {
      id: 'flat_details',
      title: 'Flat Details',
      fields: ['flatFloorNumber', 'corridorHeating']
    },
    {
      id: 'additional',
      title: 'Additional Notes',
      fields: ['additionalNotes']
    }
  ];
  
  // Apply custom section order if provided
  const fieldSections = React.useMemo(() => {
    if (!sectionOrder || sectionOrder.length === 0) {
      return defaultFieldSections;
    }
    
    // Create a map for quick lookup
    const sectionMap = new Map(defaultFieldSections.map(s => [s.id, s]));
    const orderedSections: typeof defaultFieldSections = [];
    
    // Add sections in the specified order
    sectionOrder.forEach(sectionId => {
      const section = sectionMap.get(sectionId);
      if (section) {
        orderedSections.push(section);
        sectionMap.delete(sectionId); // Remove from map
      }
    });
    
    // Add any remaining sections that weren't in the order (at the end)
    sectionMap.forEach(section => {
      orderedSections.push(section);
    });
    
    return orderedSections;
  }, [sectionOrder]);
  
  // Default field order (flattened from sections)
  const defaultFieldOrder = fieldSections.flatMap(section => section.fields);
  
  // Use provided field order or default
  const actualFieldOrder = fieldOrder || defaultFieldOrder;
  
  const [formData, setFormData] = useState({
    // Basic Information
    surveyorName: surveyData?.initial_questionnaire?.surveyorName || '',
    surveyDate: surveyData?.initial_questionnaire?.surveyDate || '',
    customerFullName: surveyData?.initial_questionnaire?.customerFullName || '',
    customerEmail: surveyData?.initial_questionnaire?.customerEmail || '',
    customerMobile: surveyData?.initial_questionnaire?.customerMobile || '',
    buildingName: surveyData?.initial_questionnaire?.buildingName || '',
    houseNumberName: surveyData?.initial_questionnaire?.houseNumberName || '',
    streetAddress: surveyData?.initial_questionnaire?.streetAddress || '',
    town: surveyData?.initial_questionnaire?.town || '',
    postCode: surveyData?.initial_questionnaire?.postCode || '',
    
    // Property Details
    habitableRooms: surveyData?.initial_questionnaire?.habitableRooms || '',
    heatedHabitableRooms: surveyData?.initial_questionnaire?.heatedHabitableRooms || '',
    tenure: surveyData?.initial_questionnaire?.tenure || '',
    buildingAge: surveyData?.initial_questionnaire?.buildingAge || '',
    dwellingType: surveyData?.initial_questionnaire?.dwellingType || '',
    buildingOrientation: surveyData?.initial_questionnaire?.buildingOrientation || '',
    majorityWallType: surveyData?.initial_questionnaire?.majorityWallType || '',
    
    // Flat-specific fields
    flatFloorNumber: surveyData?.initial_questionnaire?.flatFloorNumber || '',
    corridorHeating: surveyData?.initial_questionnaire?.corridorHeating || '',
    
    // Utility Information
    hasMainsGas: surveyData?.initial_questionnaire?.hasMainsGas ?? null,
    hasOilLpgTank: surveyData?.initial_questionnaire?.hasOilLpgTank ?? null,
    hasRenewables: surveyData?.initial_questionnaire?.hasRenewables ?? null,
    
    // Additional Notes
    additionalNotes: surveyData?.initial_questionnaire?.additionalNotes || ''
  });

  const [photos, setPhotos] = useState<Record<string, PhotoSection>>({
    orientationImage: { photos: [] },
    electricMeter: { photos: [] },
    consumerBoard: { photos: [] },
    stopTap: { photos: [] },
    gasMeterEarthBonding: { photos: [] },
    oilLpgTankImage: { photos: [] },
    renewablesImage: { photos: [] },
    corridorStairwayImages: { photos: [] } // For flat corridor/stairway photos
  });
  
  // Track photos that have been processed for migration
  const migratedPhotoIdsRef = useRef<Set<string>>(new Set());
  // Track if migration has run
  const migrationRunOnceRef = useRef(false);
  
  // Initialize photo upload queue with explicit surveyId and property name for better file organization
  const propertyName = surveyData ? getRetrofitSurveyAddress(surveyData) : undefined;
  const { queuePhotoUpload } = useRetrofitPhotoUpload(
    surveyId || surveyData?.id || floorPlanId,
    propertyName
  );
  
  const [selectedGallery, setSelectedGallery] = useState<string | null>(null);

  // Update form data when surveyData prop changes
  useEffect(() => {
    // Only restore if this is a new survey or initial load
    const currentSurveyId: string | null = surveyData?.id || surveyId || null;
    const shouldRestore = surveyData?.initial_questionnaire && 
                         currentSurveyId !== null &&
                         lastRestoredSurveyId.current !== currentSurveyId;
    
    if (shouldRestore) {
      console.log('[RetrofitSurveyForm] Restoring form data for survey:', currentSurveyId);
      lastRestoredSurveyId.current = currentSurveyId as string;
      isRestoringFromProps.current = true;
      
      // Restore built-in form data
      const restoredFormData: any = {
        surveyorName: surveyData.initial_questionnaire.surveyorName || '',
        surveyDate: surveyData.initial_questionnaire.surveyDate || '',
        customerFullName: surveyData.initial_questionnaire.customerFullName || '',
        customerEmail: surveyData.initial_questionnaire.customerEmail || '',
        customerMobile: surveyData.initial_questionnaire.customerMobile || '',
        buildingName: surveyData.initial_questionnaire.buildingName || '',
        houseNumberName: surveyData.initial_questionnaire.houseNumberName || '',
        streetAddress: surveyData.initial_questionnaire.streetAddress || '',
        town: surveyData.initial_questionnaire.town || '',
        postCode: surveyData.initial_questionnaire.postCode || '',
        habitableRooms: surveyData.initial_questionnaire.habitableRooms || '',
        heatedHabitableRooms: surveyData.initial_questionnaire.heatedHabitableRooms || '',
        tenure: surveyData.initial_questionnaire.tenure || '',
        buildingAge: surveyData.initial_questionnaire.buildingAge || '',
        dwellingType: surveyData.initial_questionnaire.dwellingType || '',
        buildingOrientation: surveyData.initial_questionnaire.buildingOrientation || '',
        majorityWallType: surveyData.initial_questionnaire.majorityWallType || '',
        flatFloorNumber: surveyData.initial_questionnaire.flatFloorNumber || '',
        corridorHeating: surveyData.initial_questionnaire.corridorHeating || '',
        hasMainsGas: surveyData.initial_questionnaire.hasMainsGas ?? null,
        hasOilLpgTank: surveyData.initial_questionnaire.hasOilLpgTank ?? null,
        hasRenewables: surveyData.initial_questionnaire.hasRenewables ?? null,
        additionalNotes: surveyData.initial_questionnaire.additionalNotes || ''
      };
      
      // Also restore custom field values (including signatures, photos, and boolean photos)
      customFields.forEach(field => {
        // Restore the field value
        if (field.id in surveyData.initial_questionnaire) {
          restoredFormData[field.id] = (surveyData.initial_questionnaire as any)[field.id];
          
          // Debug: Log photo field restoration
          if (field.type === 'image' || (field.type === 'boolean' && (field as any).enablePhotoCapture)) {
            const photoFieldId = field.type === 'boolean' ? `${field.id}_photos` : field.id;
            const photoValue = (surveyData.initial_questionnaire as any)[photoFieldId];
            if (Array.isArray(photoValue) && photoValue.length > 0) {
              console.log(`[RetrofitSurveyForm] 📸 Restoring photos for ${photoFieldId}:`, {
                count: photoValue.length,
                firstPhotoType: photoValue[0]?.startsWith('data:') ? 'data URL' : 
                               photoValue[0]?.startsWith('http') ? 'CDN URL' : 'unknown',
                firstPhotoPreview: photoValue[0]?.substring(0, 50)
              });
            }
          }
        }
        
        // For boolean fields with photo capture, also restore the photos
        if (field.type === 'boolean' && (field as any).enablePhotoCapture) {
          const photosFieldId = `${field.id}_photos`;
          if (photosFieldId in surveyData.initial_questionnaire) {
            restoredFormData[photosFieldId] = (surveyData.initial_questionnaire as any)[photosFieldId];
            console.log(`[RetrofitSurveyForm] 📸 Restored boolean photos for ${photosFieldId}:`, {
              count: restoredFormData[photosFieldId]?.length,
              type: Array.isArray(restoredFormData[photosFieldId]) ? 
                    (restoredFormData[photosFieldId][0]?.startsWith('http') ? 'CDN URLs' : 'data URLs') : 
                    'not an array'
            });
          }
        }
        
        // For signature fields, also restore the date
        if (field.type === 'signature') {
          const dateFieldId = field.id + 'Date';
          if (dateFieldId in surveyData.initial_questionnaire) {
            restoredFormData[dateFieldId] = (surveyData.initial_questionnaire as any)[dateFieldId];
          }
        }
      });
      
      // Also scan for any orphaned *_photos fields that might not be in customFields
      Object.keys(surveyData.initial_questionnaire).forEach(key => {
        if (key.endsWith('_photos') && !(key in restoredFormData)) {
          restoredFormData[key] = (surveyData.initial_questionnaire as any)[key];
          console.log(`[RetrofitSurveyForm] 📸 Restored orphaned photo field ${key}`);
        }
      });
      
      console.log('[RetrofitSurveyForm] Restored formData with custom fields:', {
        customFieldCount: customFields.length,
        restoredCustomFieldIds: customFields.map(f => f.id),
        hasSignatureData: customFields.some(f => f.type === 'signature' && restoredFormData[f.id])
      });
      
      setFormData(restoredFormData);
      
      // Restore photos if they exist - merge with default structure to ensure all sections exist
      if (surveyData.initial_questionnaire.photos) {
        console.log('[RetrofitSurveyForm] Restoring photos from surveyData:', surveyData.initial_questionnaire.photos);
        
        // Helper to clear stale uploading flags from previous sessions
        const clearUploadingFlags = (section: PhotoSection | undefined): PhotoSection => {
          if (!section?.photos || !Array.isArray(section.photos)) {
            return { photos: [] };
          }
          return {
            photos: section.photos.map((photo: any) => ({
              ...photo,
              uploading: false,
              error: undefined
            }))
          };
        };
        
        setPhotos(prev => ({
          orientationImage: clearUploadingFlags(surveyData.initial_questionnaire.photos.orientationImage) || prev.orientationImage,
          electricMeter: clearUploadingFlags(surveyData.initial_questionnaire.photos.electricMeter) || prev.electricMeter,
          consumerBoard: clearUploadingFlags(surveyData.initial_questionnaire.photos.consumerBoard) || prev.consumerBoard,
          stopTap: clearUploadingFlags(surveyData.initial_questionnaire.photos.stopTap) || prev.stopTap,
          gasMeterEarthBonding: clearUploadingFlags(surveyData.initial_questionnaire.photos.gasMeterEarthBonding) || prev.gasMeterEarthBonding,
          oilLpgTankImage: clearUploadingFlags(surveyData.initial_questionnaire.photos.oilLpgTankImage) || prev.oilLpgTankImage,
          renewablesImage: clearUploadingFlags(surveyData.initial_questionnaire.photos.renewablesImage) || prev.renewablesImage,
          corridorStairwayImages: clearUploadingFlags(surveyData.initial_questionnaire.photos.corridorStairwayImages) || prev.corridorStairwayImages
        }));
      }
      
      // Reset flag after state update completes
      setTimeout(() => {
        isRestoringFromProps.current = false;
      }, 0);
    }
  }, [surveyData, customFields]);

  // Auto-convert base64 photos to CDN URLs on load (backward compatibility)
  useEffect(() => {
    if (!photos || !onDataUpdate || !queuePhotoUpload) {
      return;
    }
    
    // Quick pre-check: Do we have ANY photos that need migration?
    let needsMigration = false;
    (Object.keys(photos) as Array<keyof typeof photos>).forEach(photoSection => {
      const section = photos[photoSection];
      if (!section?.photos || !Array.isArray(section.photos)) return;
      
      section.photos.forEach((photo: any) => {
        if ((photo.dataUrl || photo.localPath) && (!photo.url || photo.url === '')) {
          needsMigration = true;
        }
      });
    });
    
    if (!needsMigration) {
      console.log('[RetrofitSurveyForm] ✅ All photos already have CDN URLs, skipping migration');
      return;
    }
    
    if (migrationRunOnceRef.current) {
      console.log('[RetrofitSurveyForm] ⏭️ Migration already executed (Strict Mode protection)');
      return;
    }
    
    const currentSurveyId = surveyId || surveyData?.id;
    console.log('[RetrofitSurveyForm] 🚀 Starting migration for survey:', currentSurveyId);
    migrationRunOnceRef.current = true;
    
    let hasBase64Photos = false;
    
    // Check all photo sections
    (Object.keys(photos) as Array<keyof typeof photos>).forEach(photoSection => {
      const section = photos[photoSection];
      if (!section?.photos || !Array.isArray(section.photos)) return;
      
      section.photos.forEach((photo: any) => {
        // Skip if already processed
        if (migratedPhotoIdsRef.current.has(photo.id) || photo.uploading) {
          return;
        }
        
        const needsPhotoMigration = (photo.dataUrl || photo.localPath) && (!photo.url || photo.url === '');
        
        if (needsPhotoMigration) {
          hasBase64Photos = true;
          migratedPhotoIdsRef.current.add(photo.id);
          
          const dataUrl = photo.dataUrl || photo.localPath;
          console.log(`[RetrofitSurveyForm] 🔄 Migrating base64 photo to CDN:`, photo.id);
          
          queuePhotoUpload(
            photo.id,
            dataUrl,
            'initial_questionnaire',
            photoSection as string,
            // On success: replace base64 with CDN URL
            async (url: string) => {
              setPhotos(current => {
                const updated = { ...current };
                const sectionData = updated[photoSection];
                if (sectionData?.photos) {
                  sectionData.photos = sectionData.photos.map(p =>
                    p.id === photo.id ? { id: p.id, url, uploading: false } : p
                  );
                }
                
                // Sync to parent
                if (onDataUpdate) {
                  setTimeout(() => {
                    onDataUpdate(prevData => ({
                      ...prevData,
                      initial_questionnaire: {
                        ...prevData.initial_questionnaire,
                        photos: updated
                      }
                    }));
                  }, 0);
                }
                
                return updated;
              });
              console.log(`[RetrofitSurveyForm] ✅ Migration complete: ${photo.id} → CDN URL`);
            },
            // On error: keep base64
            (error: string) => {
              console.error(`[RetrofitSurveyForm] ⚠️ Migration failed for ${photo.id}, keeping base64:`, error);
            }
          );
        }
      });
    });
    
    if (hasBase64Photos) {
      console.log('[RetrofitSurveyForm] 🔄 Starting automatic base64 → CDN migration for existing photos');
    }
    
    // Cleanup: reset flag when component unmounts
    return () => {
      migrationRunOnceRef.current = false;
      migratedPhotoIdsRef.current.clear();
    };
  }, [photos, onDataUpdate, queuePhotoUpload, surveyId, surveyData?.id]);

  // No longer need formData sync useEffect - handlers sync immediately
  
  // Map photo sections to indices for usePhotoHandling compatibility
  const photoSectionKeys = Object.keys(photos);
  
  // Adapter functions for usePhotoHandling (maps photo sections to room/item pattern)
  const updatePhotoSection = useCallback((sectionIndex: number, _itemIndex: number, updates: any) => {
    const sectionKey = photoSectionKeys[sectionIndex];
    if (!sectionKey) return;
    
    setPhotos(prev => {
      const updated = { ...prev };
      
      if ('photoToUpdate' in updates) {
        const photoToUpdate = updates.photoToUpdate as PhotoField;
        const section = updated[sectionKey];
        const photoIndex = section.photos.findIndex(p => p.id === photoToUpdate.id);
        
        if (photoIndex !== -1) {
          section.photos[photoIndex] = photoToUpdate;
        } else {
          section.photos.push(photoToUpdate);
        }
      } else if ('photos' in updates) {
        updated[sectionKey] = { photos: updates.photos as PhotoField[] };
      }
      
      // Sync to parent for autosave
      if (onDataUpdate && !isRestoringFromProps.current) {
        setTimeout(() => {
          onDataUpdate(prevData => ({
            ...prevData,
            initial_questionnaire: {
              ...prevData.initial_questionnaire,
              photos: updated
            }
          }));
        }, 0);
      }
      
      return updated;
    });
  }, [photoSectionKeys, onDataUpdate]);
  
  const getPhotoSectionPhotos = useCallback((sectionIndex: number, _itemIndex: number): RoomPhoto[] => {
    const sectionKey = photoSectionKeys[sectionIndex];
    if (!sectionKey) return [];
    return (photos[sectionKey]?.photos || []) as RoomPhoto[];
  }, [photoSectionKeys, photos]);
  
  const getCurrentSurveyId = useCallback(() => {
    return surveyId || floorPlanId || surveyData?.id || null;
  }, [surveyId, floorPlanId, surveyData?.id]);
  
  // Integrate usePhotoHandling with all optimizations
  const { handlePhotoUploadFromDataUrls } = usePhotoHandling({
    updateRoomItem: updatePhotoSection,
    getItemPhotos: getPhotoSectionPhotos,
    getCurrentInventoryId: getCurrentSurveyId,
    onBatchUploadStart,
    onBatchUploadEnd
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    
    const updatedData = type === 'checkbox' 
      ? { [name]: (e.target as HTMLInputElement).checked }
      : { [name]: value };
    
    // Log address-related field changes
    if (['buildingName', 'town', 'postCode'].includes(name)) {
      console.log(`[RetrofitSurvey] ${name} changed:`, value);
    }
    
    setFormData(prev => ({
      ...prev,
      ...updatedData
    }));
    
    // Sync to parent immediately for autosave (unless restoring from props)
    if (onDataUpdate && !isRestoringFromProps.current) {
      onDataUpdate(prev => ({
        ...prev,
        initial_questionnaire: {
          ...prev.initial_questionnaire,
          ...updatedData
        }
      }), 'initial_questionnaire');
    }
  };

  const handleBooleanChange = (name: string, value: boolean) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Sync to parent immediately for autosave (unless restoring from props)
    if (onDataUpdate && !isRestoringFromProps.current) {
      onDataUpdate(prev => ({
        ...prev,
        initial_questionnaire: {
          ...prev.initial_questionnaire,
          [name]: value
        }
      }), 'initial_questionnaire');
    }
  };

  const handlePhotosCaptured = (photoType: string, photoData: PhotoData[]) => {
    if (photoData.length > 0) {
      // Convert PhotoData to base64 data URLs for usePhotoHandling
      const dataUrls = photoData.map(photo => 
        `data:image/${photo.format};base64,${photo.base64String}`
      );
      
      // Find the section index for this photo type
      const sectionIndex = photoSectionKeys.indexOf(photoType);
      if (sectionIndex === -1) {
        console.error(`[RetrofitSurvey] Unknown photo section: ${photoType}`);
        return;
      }
      
      // Use handlePhotoUploadFromDataUrls directly with data URLs
      // This triggers: parallel compression + Supabase upload + suspended autosave
      handlePhotoUploadFromDataUrls(dataUrls, sectionIndex, 0);
    }
  };
  
  const handleDeletePhoto = (photoType: string, photoId: string) => {
    setPhotos(prev => {
      const updated = {
        ...prev,
        [photoType]: {
          photos: prev[photoType].photos.filter(p => p.id !== photoId)
        }
      };
      
      // Sync to parent after state update completes
      if (onDataUpdate && !isRestoringFromProps.current) {
        setTimeout(() => {
          onDataUpdate(prevData => ({
            ...prevData,
            initial_questionnaire: {
              ...prevData.initial_questionnaire,
              photos: updated
            }
          }));
        }, 0);
      }
      
      return updated;
    });
  };
  
  const handleUpdatePhotoNote = (photoType: string, photoId: string, note: string) => {
    setPhotos(prev => {
      const updated = {
        ...prev,
        [photoType]: {
          photos: prev[photoType].photos.map(p => 
            p.id === photoId ? { ...p, note } : p
          )
        }
      };
      
      // Sync to parent after state update completes
      if (onDataUpdate && !isRestoringFromProps.current) {
        setTimeout(() => {
          onDataUpdate(prevData => ({
            ...prevData,
            initial_questionnaire: {
              ...prevData.initial_questionnaire,
              photos: updated
            }
          }));
        }, 0);
      }
      
      return updated;
    });
  };
  
  const handleOpenGallery = (e: React.MouseEvent<HTMLButtonElement>, photoType: string) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Blur any active input to dismiss keyboard
    if (document.activeElement && document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    
    setSelectedGallery(photoType);
    onGalleryStateChange?.(true);
  };
  
  const handleCloseGallery = () => {
    setSelectedGallery(null);
    onGalleryStateChange?.(false);
  };

  const renderPhotoPreview = (photoSection: PhotoSection, photoType: string, altTextPrefix: string) => {
    const photoArray = photoSection?.photos || [];
    if (photoArray.length === 0) return null;

    // Use CDN URL if available, otherwise use dataUrl
    const latestPhoto = photoArray[photoArray.length - 1];
    const imageUrl = latestPhoto.url || (latestPhoto as any).dataUrl;
    
    if (!imageUrl) return null;

    return (
      <button
        type="button"
        onClick={(e) => handleOpenGallery(e, photoType)}
        className="relative w-20 h-20 rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all hover:scale-105 ring-2 ring-slate-300 hover:ring-yellow-400 flex-shrink-0 mt-2"
      >
        <img
          src={imageUrl}
          alt={altTextPrefix}
          className="w-full h-full object-cover"
        />
        <div className="absolute top-1 right-1 bg-gradient-to-r from-yellow-500 to-orange-600 text-white text-xs font-bold px-2 py-1 rounded-full shadow-lg flex items-center gap-1">
          <Images size={12} />
          {photoArray.length}
        </div>
      </button>
    );
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Initial Questionnaire Data:', { formData, photos });
    onNavigateNext();
  };

  // Helper function to render a field based on its ID
  const renderField = (fieldId: string) => {
    if (isFieldHidden(fieldId)) return null;

    switch (fieldId) {
      case 'surveyDate':
        return (
          <div key="surveyDate" className="space-y-2">
            <label htmlFor="surveyDate" className="text-sm font-semibold text-slate-700 flex items-center gap-1.5">
              <span className="text-rose-500">*</span>
              Survey Date
            </label>
            <div className="max-w-[160px]">
              <input
                type="date"
                id="surveyDate"
                name="surveyDate"
                value={formData.surveyDate}
                onChange={handleInputChange}
                className="w-full px-3 py-3 border-2 border-gray-200 rounded-lg 
                         focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-yellow-400
                         transition-all duration-200
                         [&::-webkit-date-and-time-value]:text-left
                         [&::-webkit-calendar-picker-indicator]:cursor-pointer"
                required
              />
            </div>
          </div>
        );

      case 'surveyorName':
        return (
          <div key="surveyorName" className="space-y-2">
            <label htmlFor="surveyorName" className="text-sm font-medium text-gray-700 flex items-center">
              <span className="text-red-500 mr-1">*</span>
              Surveyor name
            </label>
            <input
              type="text"
              id="surveyorName"
              name="surveyorName"
              value={formData.surveyorName}
              onChange={handleInputChange}
              placeholder="enter here"
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-yellow-400"
              required
            />
          </div>
        );

      case 'customerFullName':
        return (
          <div key="customerFullName" className="space-y-2">
            <label htmlFor="customerFullName" className="text-sm font-medium text-gray-700 flex items-center">
              <span className="text-red-500 mr-1">*</span>
              Customer Full name
            </label>
            <input
              type="text"
              id="customerFullName"
              name="customerFullName"
              value={formData.customerFullName}
              onChange={handleInputChange}
              placeholder="enter here"
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-yellow-400"
              required
            />
          </div>
        );

      case 'customerEmail':
        return (
          <div key="customerEmail" className="space-y-2">
            <label htmlFor="customerEmail" className="text-sm font-medium text-gray-700 flex items-center">
              <span className="text-red-500 mr-1">*</span>
              Customer Email
            </label>
            <input
              type="email"
              id="customerEmail"
              name="customerEmail"
              value={formData.customerEmail}
              onChange={handleInputChange}
              placeholder="enter here"
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-yellow-400"
              required
            />
          </div>
        );

      case 'customerMobile':
        return (
          <div key="customerMobile" className="space-y-2">
            <label htmlFor="customerMobile" className="text-sm font-medium text-gray-700 flex items-center">
              <span className="text-red-500 mr-1">*</span>
              Customer Mobile Number
            </label>
            <input
              type="tel"
              id="customerMobile"
              name="customerMobile"
              value={formData.customerMobile}
              onChange={handleInputChange}
              placeholder="enter here"
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-yellow-400"
              required
            />
          </div>
        );

      case 'buildingName':
        return (
          <div key="buildingName" className="space-y-2">
            <label htmlFor="buildingName" className="text-sm font-medium text-gray-700 flex items-center">
              Building / Flat Name & Number
            </label>
            <input
              type="text"
              id="buildingName"
              name="buildingName"
              value={formData.buildingName}
              onChange={handleInputChange}
              placeholder="e.g., 'Flat 2 Ferndale Court'"
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-yellow-400"
            />
            <p className="text-xs text-gray-500">Optional: For flats, apartments, or buildings with names</p>
          </div>
        );

      case 'houseNumberName':
        return (
          <div key="houseNumberName" className="space-y-2">
            <label htmlFor="streetAddress" className="text-sm font-medium text-gray-700 flex items-center">
              <span className="text-red-500 mr-1">*</span>
              House Number & Street
            </label>
            <input
              type="text"
              id="streetAddress"
              name="streetAddress"
              value={
                formData.houseNumberName && formData.streetAddress
                  ? `${formData.houseNumberName} ${formData.streetAddress}`
                  : formData.streetAddress || formData.houseNumberName || ''
              }
              onChange={(e) => {
                const value = e.target.value;
                const match = value.match(/^(\d+[a-zA-Z]?)\s+(.+)$/);
                
                console.log('[RetrofitSurvey] Address field changed:', { value, hasMatch: !!match });
                
                if (match) {
                  const updates = {
                    houseNumberName: match[1],
                    streetAddress: match[2]
                  };
                  console.log('[RetrofitSurvey] Parsed address:', updates);
                  
                  setFormData(prev => ({
                    ...prev,
                    ...updates
                  }));
                  
                  // Sync to parent for autosave
                  if (onDataUpdate && !isRestoringFromProps.current) {
                    onDataUpdate(prev => ({
                      ...prev,
                      initial_questionnaire: {
                        ...prev.initial_questionnaire,
                        ...updates
                      }
                    }), 'initial_questionnaire');
                  }
                } else {
                  const updates = {
                    streetAddress: value,
                    houseNumberName: ''
                  };
                  console.log('[RetrofitSurvey] Unparsed address:', updates);
                  
                  setFormData(prev => ({
                    ...prev,
                    ...updates
                  }));
                  
                  // Sync to parent for autosave
                  if (onDataUpdate && !isRestoringFromProps.current) {
                    onDataUpdate(prev => ({
                      ...prev,
                      initial_questionnaire: {
                        ...prev.initial_questionnaire,
                        ...updates
                      }
                    }), 'initial_questionnaire');
                  }
                }
              }}
              placeholder="112 Skiffington Close"
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-yellow-400"
              required
            />
            <p className="text-xs text-gray-500">Enter house number and street together (e.g., 1 Kendra Hall Road)</p>
          </div>
        );

      case 'town':
        return (
          <div key="town" className="space-y-2">
            <label htmlFor="town" className="text-sm font-medium text-gray-700 flex items-center">
              <span className="text-red-500 mr-1">*</span>
              Town
            </label>
            <input
              type="text"
              id="town"
              name="town"
              value={formData.town}
              onChange={handleInputChange}
              placeholder="enter here"
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-yellow-400"
              required
            />
          </div>
        );

      case 'postCode':
        return (
          <div key="postCode" className="space-y-2">
            <label htmlFor="postCode" className="text-sm font-medium text-gray-700 flex items-center">
              <span className="text-red-500 mr-1">*</span>
              Post Code
            </label>
            <input
              type="text"
              id="postCode"
              name="postCode"
              value={formData.postCode}
              onChange={handleInputChange}
              placeholder="enter here"
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-yellow-400"
              required
            />
          </div>
        );

      case 'tenure':
        return (
          <div key="tenure" className="space-y-2">
            <label htmlFor="tenure" className="text-sm font-medium text-gray-700 flex items-center">
              <span className="text-red-500 mr-1">*</span>
              Tenure
            </label>
            <select
              id="tenure"
              name="tenure"
              value={formData.tenure}
              onChange={handleInputChange}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-yellow-400"
              required
            >
              <option value="">Select...</option>
              <option value="owner-occupied">Owner-occupied</option>
              <option value="rented-private">Rented (private)</option>
              <option value="rented-social">Rented (social)</option>
            </select>
          </div>
        );

      case 'buildingAge':
        return (
          <div key="buildingAge" className="space-y-2">
            <label htmlFor="buildingAge" className="text-sm font-medium text-gray-700 flex items-center">
              <span className="text-red-500 mr-1">*</span>
              Building Age
            </label>
            <select
              id="buildingAge"
              name="buildingAge"
              value={formData.buildingAge}
              onChange={handleInputChange}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-yellow-400"
              required
            >
              <option value="">Select...</option>
              <option value="pre-1900">Before 1900</option>
              <option value="1900-1929">1900-1929</option>
              <option value="1930-1949">1930-1949</option>
              <option value="1950-1966">1950-1966</option>
              <option value="1967-1975">1967-1975</option>
              <option value="1976-1982">1976-1982</option>
              <option value="1983-1990">1983-1990</option>
              <option value="1991-1995">1991-1995</option>
              <option value="1996-2002">1996-2002</option>
              <option value="2003-2006">2003-2006</option>
              <option value="2007-present">2007-present</option>
            </select>
          </div>
        );

      case 'dwellingType':
        return (
          <div key="dwellingType" className="space-y-2">
            <label htmlFor="dwellingType" className="text-sm font-medium text-gray-700 flex items-center">
              <span className="text-red-500 mr-1">*</span>
              Dwelling Type
            </label>
            <select
              id="dwellingType"
              name="dwellingType"
              value={formData.dwellingType}
              onChange={handleInputChange}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-yellow-400"
              required
            >
              <option value="">Select...</option>
              <option value="house">House</option>
              <option value="bungalow">Bungalow</option>
              <option value="flat">Flat</option>
              <option value="maisonette">Maisonette</option>
              <option value="park-home">Park home</option>
            </select>
          </div>
        );

      case 'flatFloorNumber':
        // Only show for flats and maisonettes
        if (!formData.dwellingType || !['flat', 'maisonette'].includes(formData.dwellingType.toLowerCase())) {
          return null;
        }
        return (
          <div key="flatFloorNumber" className="space-y-2">
            <label htmlFor="flatFloorNumber" className="text-sm font-medium text-gray-700">
              Floor Number (for flats)
            </label>
            <input
              type="number"
              id="flatFloorNumber"
              name="flatFloorNumber"
              value={formData.flatFloorNumber}
              onChange={handleInputChange}
              placeholder="0 for ground floor"
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-yellow-400"
            />
          </div>
        );

      case 'corridorHeating':
        // Only show for flats and maisonettes
        if (!formData.dwellingType || !['flat', 'maisonette'].includes(formData.dwellingType.toLowerCase())) {
          return null;
        }
        return (
          <div key="corridorHeating" className="space-y-2">
            <label htmlFor="corridorHeating" className="text-sm font-medium text-gray-700">
              Corridor Heating (for flats)
            </label>
            <select
              id="corridorHeating"
              name="corridorHeating"
              value={formData.corridorHeating}
              onChange={handleInputChange}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-yellow-400"
            >
              <option value="">Select...</option>
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </select>
          </div>
        );

      case 'majorityWallType':
        return (
          <div key="majorityWallType" className="space-y-2">
            <label htmlFor="majorityWallType" className="text-sm font-medium text-gray-700">
              Majority Wall Type
            </label>
            <select
              id="majorityWallType"
              name="majorityWallType"
              value={formData.majorityWallType}
              onChange={handleInputChange}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-yellow-400"
            >
              <option value="">Select...</option>
              <option value="cavity">Cavity Wall</option>
              <option value="solid-brick">Solid Brick</option>
              <option value="solid-stone">Solid Stone</option>
              <option value="solid-concrete">Solid Concrete</option>
              <option value="timber-frame">Timber Frame</option>
              <option value="system-built">System Built</option>
              <option value="other">Other</option>
            </select>
          </div>
        );

      case 'additionalNotes':
        return (
          <div key="additionalNotes" className="space-y-2">
            <label htmlFor="additionalNotes" className="text-sm font-medium text-gray-700">
              Additional Notes
            </label>
            <textarea
              id="additionalNotes"
              name="additionalNotes"
              value={formData.additionalNotes}
              onChange={handleInputChange}
              rows={4}
              placeholder="Any additional information..."
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-yellow-400"
            />
          </div>
        );

      default:
        // Check if it's a custom field
        const customField = customFields.find(f => f.id === fieldId);
        if (customField) {
          return renderCustomField(customField);
        }
        return null;
    }
  };

  // Helper to render photo capture section for any field type
  const renderFieldPhotoCapture = (field: FieldSchema) => {
    const fieldConfig = field as any;
    const enablePhotoCapture = fieldConfig.enablePhotoCapture || false;
    const maxFieldPhotos = fieldConfig.maxPhotos || 10;
    const photosFieldId = `${field.id}_photos`;
    const fieldPhotos = Array.isArray((formData as any)[photosFieldId]) 
      ? (formData as any)[photosFieldId] 
      : [];
    
    if (!enablePhotoCapture) return null;
    
    // Helper to format image URLs
    const formatFieldImageUrl = (url: string) => {
      if (!url) return '';
      if (url.startsWith('data:') || url.startsWith('http')) return url;
      return `data:image/webp;base64,${url}`;
    };
    
    // Render photo preview thumbnail
    const renderFieldPhotoPreview = () => {
      if (fieldPhotos.length === 0) return null;
      const latestImage = fieldPhotos[fieldPhotos.length - 1];
      const imageUrl = formatFieldImageUrl(latestImage);
      if (!imageUrl) return null;
      
      return (
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            setSelectedGallery(photosFieldId);
          }}
          className="relative w-20 h-20 rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all hover:scale-105 ring-2 ring-slate-300 hover:ring-yellow-400 flex-shrink-0 mt-2"
        >
          <img
            src={imageUrl}
            alt={`${field.label} photos`}
            className="w-full h-full object-cover"
            onError={(e) => {
              (e.target as HTMLImageElement).src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100"%3E%3Crect fill="%23ddd" width="100" height="100"/%3E%3Ctext fill="%23999" x="50%" y="50%" text-anchor="middle" dy=".3em"%3EError%3C/text%3E%3C/svg%3E';
            }}
          />
          <div className="absolute top-1 right-1 bg-gradient-to-r from-yellow-500 to-orange-600 text-white text-xs font-bold px-2 py-1 rounded-full shadow-lg flex items-center gap-1">
            <Images size={12} />
            {fieldPhotos.length}
          </div>
        </button>
      );
    };
    
    return (
      <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
        <p className="text-xs text-gray-600 mb-2">📸 Attached Photos</p>
        <PhotoCapture
          onPhotosCaptured={(newPhotos: PhotoData[]) => {
            console.log(`[Field Photo Capture] Captured ${newPhotos.length} photos for field: ${field.id}`);
            const newPhotoUrls = newPhotos.map(p => {
              if (p.base64String.startsWith('data:')) return p.base64String;
              return `data:image/${p.format || 'webp'};base64,${p.base64String}`;
            });
            const updatedPhotos = [...fieldPhotos, ...newPhotoUrls];
            console.log(`[Field Photo Capture] Total photos now: ${updatedPhotos.length}`);
            const event = {
              target: {
                name: photosFieldId,
                value: updatedPhotos
              }
            } as any;
            handleInputChange(event);
          }}
          maxPhotos={maxFieldPhotos}
          buttonLabel="Attach Photos"
          allowUpload={true}
        />
        {renderFieldPhotoPreview()}
      </div>
    );
  };

  // Render custom field based on its type
  const renderCustomField = (field: FieldSchema) => {
    const value = (formData as any)[field.id] || '';
    
    switch (field.type) {
      case 'text':
        return (
          <div key={field.id} className="space-y-2">
            <label htmlFor={field.id} className="text-sm font-medium text-gray-700 flex items-center">
              {field.required && <span className="text-red-500 mr-1">*</span>}
              {field.label}
            </label>
            <input
              type="text"
              id={field.id}
              name={field.id}
              value={value}
              onChange={handleInputChange}
              placeholder={field.placeholder || ''}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-yellow-400"
              required={field.required}
            />
            {field.helpText && <p className="text-xs text-gray-500">{field.helpText}</p>}
            {renderFieldPhotoCapture(field)}
          </div>
        );
      
      case 'textarea':
        return (
          <div key={field.id} className="space-y-2">
            <label htmlFor={field.id} className="text-sm font-medium text-gray-700 flex items-center">
              {field.required && <span className="text-red-500 mr-1">*</span>}
              {field.label}
            </label>
            <textarea
              id={field.id}
              name={field.id}
              value={value}
              onChange={handleInputChange}
              placeholder={field.placeholder || ''}
              rows={(field as any).rows || 4}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-yellow-400"
              required={field.required}
            />
            {field.helpText && <p className="text-xs text-gray-500">{field.helpText}</p>}
            {renderFieldPhotoCapture(field)}
          </div>
        );
      
      case 'number':
        return (
          <div key={field.id} className="space-y-2">
            <label htmlFor={field.id} className="text-sm font-medium text-gray-700 flex items-center">
              {field.required && <span className="text-red-500 mr-1">*</span>}
              {field.label} {(field as any).unit && <span className="text-gray-500">({(field as any).unit})</span>}
            </label>
            <input
              type="number"
              id={field.id}
              name={field.id}
              value={value}
              onChange={handleInputChange}
              min={(field as any).min}
              max={(field as any).max}
              step={(field as any).step || 1}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-yellow-400"
              required={field.required}
            />
            {field.helpText && <p className="text-xs text-gray-500">{field.helpText}</p>}
            {renderFieldPhotoCapture(field)}
          </div>
        );
      
      case 'dropdown':
        return (
          <div key={field.id} className="space-y-2">
            <label htmlFor={field.id} className="text-sm font-medium text-gray-700 flex items-center">
              {field.required && <span className="text-red-500 mr-1">*</span>}
              {field.label}
            </label>
            <select
              id={field.id}
              name={field.id}
              value={value}
              onChange={handleInputChange}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-yellow-400"
              required={field.required}
            >
              <option value="">Select...</option>
              {((field as any).options || []).map((opt: any) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            {field.helpText && <p className="text-xs text-gray-500">{field.helpText}</p>}
            {renderFieldPhotoCapture(field)}
          </div>
        );
      
      case 'date':
        return (
          <div key={field.id} className="space-y-2">
            <label htmlFor={field.id} className="text-sm font-medium text-gray-700 flex items-center">
              {field.required && <span className="text-red-500 mr-1">*</span>}
              {field.label}
            </label>
            <input
              type="date"
              id={field.id}
              name={field.id}
              value={value}
              onChange={handleInputChange}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-yellow-400"
              required={field.required}
            />
            {field.helpText && <p className="text-xs text-gray-500">{field.helpText}</p>}
            {renderFieldPhotoCapture(field)}
          </div>
        );
      
      case 'radio':
        return (
          <div key={field.id} className="space-y-2">
            <label className="text-sm font-medium text-gray-700 flex items-center">
              {field.required && <span className="text-red-500 mr-1">*</span>}
              {field.label}
            </label>
            {field.helpText && <p className="text-xs text-gray-500">{field.helpText}</p>}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-3 mt-2">
              {((field as any).options || []).map((option: any) => (
                <label key={option.value} className="flex items-center space-x-2 p-2 border border-gray-200 rounded-md hover:bg-gray-50 cursor-pointer">
                  <input
                    type="radio"
                    name={field.id}
                    value={option.value}
                    checked={value === option.value}
                    onChange={handleInputChange}
                    className="form-radio text-yellow-500 focus:ring-yellow-400"
                    required={field.required}
                  />
                  <span className="text-sm text-gray-700">{option.label}</span>
                </label>
              ))}
            </div>
            {field.helpText && <p className="text-xs text-gray-500 mt-2">{field.helpText}</p>}
            {renderFieldPhotoCapture(field)}
          </div>
        );
      
      case 'checkbox':
        // For checkboxes, store value as array
        const checkboxValues = Array.isArray(value) ? value : (value ? [value] : []);
        return (
          <div key={field.id} className="space-y-2">
            <label className="text-sm font-medium text-gray-700 flex items-center">
              {field.required && <span className="text-red-500 mr-1">*</span>}
              {field.label}
            </label>
            {field.helpText && <p className="text-xs text-gray-500">{field.helpText}</p>}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-3 mt-2">
              {((field as any).options || []).map((option: any) => (
                <label key={option.value} className="flex items-center space-x-2 p-2 border border-gray-200 rounded-md hover:bg-gray-50 cursor-pointer">
                  <input
                    type="checkbox"
                    name={field.id}
                    value={option.value}
                    checked={checkboxValues.includes(option.value)}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      const optionValue = e.target.value;
                      let newValues: string[];
                      
                      if (checked) {
                        newValues = [...checkboxValues, optionValue];
                      } else {
                        newValues = checkboxValues.filter(v => v !== optionValue);
                      }
                      
                      const event = {
                        target: {
                          name: field.id,
                          value: newValues
                        }
                      } as any;
                      handleInputChange(event);
                    }}
                    className="form-checkbox text-yellow-500 focus:ring-yellow-400 rounded"
                  />
                  <span className="text-sm text-gray-700">{option.label}</span>
                </label>
              ))}
            </div>
            {field.helpText && <p className="text-xs text-gray-500 mt-2">{field.helpText}</p>}
            {renderFieldPhotoCapture(field)}
          </div>
        );
      
      case 'boolean':
        // Check if photo capture is enabled for this field
        const booleanFieldConfig = field as any;
        const enablePhotoCapture = booleanFieldConfig.enablePhotoCapture || false;
        const photoTrigger = booleanFieldConfig.photoTrigger || 'always';
        const maxBooleanPhotos = booleanFieldConfig.maxPhotos || 10;
        const photosFieldId = `${field.id}_photos`;
        const booleanPhotos = Array.isArray((formData as any)[photosFieldId]) 
          ? (formData as any)[photosFieldId] 
          : [];
        
        // Determine if photo capture should be shown
        const showPhotoCapture = enablePhotoCapture && (
          photoTrigger === 'always' ||
          (photoTrigger === 'yes' && (value === 'yes' || value === true)) ||
          (photoTrigger === 'no' && (value === 'no' || value === false))
        );
        
        // Helper to format image URLs
        const formatBooleanImageUrl = (url: string) => {
          if (!url) return '';
          if (url.startsWith('data:') || url.startsWith('http')) return url;
          return `data:image/webp;base64,${url}`;
        };
        
        // Render photo preview thumbnail
        const renderBooleanPhotoPreview = () => {
          if (booleanPhotos.length === 0) return null;
          const latestImage = booleanPhotos[booleanPhotos.length - 1];
          const imageUrl = formatBooleanImageUrl(latestImage);
          if (!imageUrl) return null;
          
          return (
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                setSelectedGallery(photosFieldId);
              }}
              className="relative w-20 h-20 rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all hover:scale-105 ring-2 ring-slate-300 hover:ring-yellow-400 flex-shrink-0 mt-2"
            >
              <img
                src={imageUrl}
                alt={`${field.label} photos`}
                className="w-full h-full object-cover"
                onError={(e) => {
                  (e.target as HTMLImageElement).src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100"%3E%3Crect fill="%23ddd" width="100" height="100"/%3E%3Ctext fill="%23999" x="50%" y="50%" text-anchor="middle" dy=".3em"%3EError%3C/text%3E%3C/svg%3E';
                }}
              />
              <div className="absolute top-1 right-1 bg-gradient-to-r from-yellow-500 to-orange-600 text-white text-xs font-bold px-2 py-1 rounded-full shadow-lg flex items-center gap-1">
                <Images size={12} />
                {booleanPhotos.length}
              </div>
            </button>
          );
        };
        
        return (
          <div key={field.id} className="space-y-2">
            <label className="text-sm font-medium text-gray-700 flex items-center">
              {field.required && <span className="text-red-500 mr-1">*</span>}
              {field.label}
            </label>
            {field.helpText && <p className="text-xs text-gray-500">{field.helpText}</p>}
            <div className="flex space-x-4 mt-2">
              <button
                type="button"
                onClick={() => {
                  const event = {
                    target: {
                      name: field.id,
                      value: 'yes'
                    }
                  } as any;
                  handleInputChange(event);
                }}
                className={`flex-1 py-3 rounded-lg border-2 ${
                  value === 'yes' || value === true
                    ? 'bg-yellow-100 border-yellow-400 text-yellow-700'
                    : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
                } transition-colors font-medium`}
              >
                Yes
              </button>
              <button
                type="button"
                onClick={() => {
                  const event = {
                    target: {
                      name: field.id,
                      value: 'no'
                    }
                  } as any;
                  handleInputChange(event);
                }}
                className={`flex-1 py-3 rounded-lg border-2 ${
                  value === 'no' || value === false
                    ? 'bg-yellow-100 border-yellow-400 text-yellow-700'
                    : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
                } transition-colors font-medium`}
              >
                No
              </button>
            </div>
            
            {/* Conditional Photo Capture */}
            {showPhotoCapture && (
              <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-xs text-gray-600 mb-2">📸 Evidence Photos</p>
                <PhotoCapture
                  onPhotosCaptured={(newPhotos: PhotoData[]) => {
                    console.log(`[Boolean Photo Capture] Captured ${newPhotos.length} photos for field: ${field.id}`);
                    const newPhotoUrls = newPhotos.map(p => {
                      if (p.base64String.startsWith('data:')) return p.base64String;
                      return `data:image/${p.format || 'webp'};base64,${p.base64String}`;
                    });
                    const updatedPhotos = [...booleanPhotos, ...newPhotoUrls];
                    console.log(`[Boolean Photo Capture] Total photos now: ${updatedPhotos.length}`);
                    const event = {
                      target: {
                        name: photosFieldId,
                        value: updatedPhotos
                      }
                    } as any;
                    handleInputChange(event);
                  }}
                  maxPhotos={maxBooleanPhotos}
                  buttonLabel="Take Evidence Photos"
                  allowUpload={true}
                />
                {renderBooleanPhotoPreview()}
              </div>
            )}
          </div>
        );
      
      case 'image':
        // Photo capture field - stores photo URLs as array, displays as stacked thumbnail + gallery
        const imageValues = Array.isArray(value) ? value : (value ? [value] : []);
        
        // Helper to ensure proper data URL format
        const formatImageUrl = (url: string) => {
          if (!url) return '';
          if (url.startsWith('data:') || url.startsWith('http')) return url;
          return `data:image/webp;base64,${url}`;
        };
        
        // Render stacked thumbnail preview (matching built-in photo fields)
        const renderCustomImagePreview = () => {
          if (imageValues.length === 0) return null;
          
          const latestImage = imageValues[imageValues.length - 1];
          const imageUrl = formatImageUrl(latestImage);
          
          if (!imageUrl) return null;
          
          return (
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                setSelectedGallery(field.id);
              }}
              className="relative w-20 h-20 rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all hover:scale-105 ring-2 ring-slate-300 hover:ring-yellow-400 flex-shrink-0 mt-2"
            >
              <img
                src={imageUrl}
                alt={field.label}
                className="w-full h-full object-cover"
                onError={(e) => {
                  (e.target as HTMLImageElement).src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100"%3E%3Crect fill="%23ddd" width="100" height="100"/%3E%3Ctext fill="%23999" x="50%" y="50%" text-anchor="middle" dy=".3em"%3EError%3C/text%3E%3C/svg%3E';
                }}
              />
              <div className="absolute top-1 right-1 bg-gradient-to-r from-yellow-500 to-orange-600 text-white text-xs font-bold px-2 py-1 rounded-full shadow-lg flex items-center gap-1">
                <Images size={12} />
                {imageValues.length}
              </div>
            </button>
          );
        };
        
        return (
          <div key={field.id} className="space-y-2">
            <label className="text-sm font-medium text-gray-700 flex items-center">
              {field.required && <span className="text-red-500 mr-1">*</span>}
              {field.label}
            </label>
            {field.helpText && <p className="text-xs text-gray-500">{field.helpText}</p>}
            <PhotoCapture
              onPhotosCaptured={(newPhotos: PhotoData[]) => {
                console.log(`[Custom Image Field] Captured ${newPhotos.length} photos for field: ${field.id}`);
                // Convert to proper data URLs
                const newPhotoUrls = newPhotos.map(p => {
                  if (p.base64String.startsWith('data:')) return p.base64String;
                  return `data:image/${p.format || 'webp'};base64,${p.base64String}`;
                });
                const updatedPhotos = [...imageValues, ...newPhotoUrls];
                console.log(`[Custom Image Field] Total photos now: ${updatedPhotos.length}`);
                const event = {
                  target: {
                    name: field.id,
                    value: updatedPhotos
                  }
                } as any;
                handleInputChange(event);
              }}
              maxPhotos={(field as any).maxPhotos || 10}
              buttonLabel={(field as any).buttonLabel || 'Take Photos'}
              allowUpload={true}
            />
            {/* Display stacked thumbnail preview */}
            {renderCustomImagePreview()}
          </div>
        );
      
      case 'signature':
        // Get the date field for this signature (stored as fieldId + 'Date')
        const dateFieldId = field.id + 'Date';
        const signatureDate = (formData as any)[dateFieldId] || '';
        
        return (
          <div key={field.id} className="space-y-2">
            <label className="text-sm font-medium text-gray-700 flex items-center">
              {field.required && <span className="text-red-500 mr-1">*</span>}
              {field.label}
            </label>
            {field.helpText && <p className="text-xs text-gray-500 mb-2">{field.helpText}</p>}
            
            {/* Signature container with border (matches Signatures page style) */}
            <div className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setCurrentSignatureField(field.id);
                    setShowSignatureModal(true);
                    
                    // Load existing signature if it exists
                    setTimeout(() => {
                      const ref = signatureRefs.current[field.id];
                      if (ref?.current && value) {
                        ref.current.fromDataURL(value);
                      }
                    }, 100);
                  }}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                >
                  <Edit size={16} />
                  {value ? 'Change Signature' : 'Add Signature'}
                </button>
                {value && (
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <img src={value} alt="Signature" className="h-16 border rounded bg-white" />
                    <span className="text-sm text-gray-600 whitespace-nowrap">Date: {signatureDate}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      
      default:
        // For other types (date, boolean, etc.), add simple text for now
        return (
          <div key={field.id} className="space-y-2">
            <label className="text-sm font-medium text-gray-700">
              {field.label} <span className="text-xs text-gray-500">(Custom {field.type})</span>
            </label>
            <input
              type="text"
              id={field.id}
              name={field.id}
              value={value}
              onChange={handleInputChange}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg"
            />
          </div>
        );
    }
  };

  return (
    <div className="bg-white/80 backdrop-blur-sm p-4 sm:p-6 rounded-xl shadow-lg border border-yellow-200 min-h-[calc(100vh-12rem)] flex flex-col">
      {!selectedGallery && (
        <h2 className="text-xl sm:text-2xl font-bold text-gray-900 border-b-2 border-gray-200 pb-3 mb-4 flex items-center gap-3">
          <ListChecks />
          Initial Questionnaire
        </h2>
      )}
      <form onSubmit={handleSubmit} className="space-y-6 flex-1 flex flex-col">
        <div className="flex-1 space-y-6">
          {/* Interleave built-in and custom sections based on sectionOrder */}
          {(() => {
            // Group custom fields by section
            const customFieldsBySection: Record<string, FieldSchema[]> = {};
            const fieldsWithoutSection: FieldSchema[] = [];
            
            if (customFields && customFields.length > 0) {
              customFields.forEach(field => {
                if (field.section) {
                  if (!customFieldsBySection[field.section]) {
                    customFieldsBySection[field.section] = [];
                  }
                  customFieldsBySection[field.section].push(field);
                } else {
                  fieldsWithoutSection.push(field);
                }
              });
              
              // Sort fields within each custom section by sectionOrder
              Object.keys(customFieldsBySection).forEach(sectionName => {
                customFieldsBySection[sectionName].sort((a, b) => (a.sectionOrder || 0) - (b.sectionOrder || 0));
              });
              
              // Sort unsectioned fields by their order property
              fieldsWithoutSection.sort((a, b) => (a.order || 0) - (b.order || 0));
            }
            
            // Create a unified list of all section identifiers (both built-in and custom)
            const allSectionIds: string[] = [];
            
            // Add built-in section IDs
            fieldSections.forEach(section => {
              allSectionIds.push(section.id);
            });
            
            // Add custom section names
            Object.keys(customFieldsBySection).forEach(sectionName => {
              allSectionIds.push(sectionName);
            });
            
            // Sort all sections by sectionOrder if provided
            if (sectionOrder && sectionOrder.length > 0) {
              allSectionIds.sort((a, b) => {
                const aIndex = sectionOrder.indexOf(a);
                const bIndex = sectionOrder.indexOf(b);
                
                if (aIndex !== -1 && bIndex !== -1) {
                  return aIndex - bIndex;
                }
                if (aIndex !== -1) return -1;
                if (bIndex !== -1) return 1;
                
                // Neither in sectionOrder - built-in sections first, then alphabetical
                const aIsBuiltIn = fieldSections.some(s => s.id === a);
                const bIsBuiltIn = fieldSections.some(s => s.id === b);
                
                if (aIsBuiltIn && !bIsBuiltIn) return -1;
                if (!aIsBuiltIn && bIsBuiltIn) return 1;
                
                return a.localeCompare(b);
              });
            }
            
            // Render sections in the unified order
            return (
              <>
                {allSectionIds.map(sectionId => {
                  // Check if this is a built-in section
                  const builtInSection = fieldSections.find(s => s.id === sectionId);
                  
                  if (builtInSection) {
                    // Render built-in section
                    const visibleFields = builtInSection.fields.filter(fieldId => !isFieldHidden(fieldId));
                    if (visibleFields.length === 0) return null;
                    
                    return (
                      <fieldset key={sectionId} className="border border-gray-300 p-4 rounded-lg">
                        <legend className="text-md font-semibold text-gray-700 px-2">{builtInSection.title}</legend>
                        <div className="space-y-4">
                          {visibleFields.map(fieldId => renderField(fieldId))}
                        </div>
                      </fieldset>
                    );
                  }
                  
                  // Check if this is a custom section
                  const customSectionFields = customFieldsBySection[sectionId];
                  if (customSectionFields && !hiddenSections.includes(sectionId)) {
                    // Render custom section
                    return (
                      <fieldset key={`custom_section_${sectionId}`} className="border border-gray-300 p-4 rounded-lg">
                        <legend className="text-md font-semibold text-gray-700 px-2">{sectionId}</legend>
                        <div className="space-y-4">
                          {customSectionFields.map(field => renderCustomField(field))}
                        </div>
                      </fieldset>
                    );
                  }
                  
                  return null;
                })}
                
                {/* Render unsectioned custom fields at the end */}
                {fieldsWithoutSection.length > 0 && (
                  <fieldset className="border border-gray-300 p-4 rounded-lg">
                    <legend className="text-md font-semibold text-gray-700 px-2">Custom Fields</legend>
                    <div className="space-y-4">
                      {fieldsWithoutSection.map(field => renderCustomField(field))}
                    </div>
                  </fieldset>
                )}
              </>
            );
          })()}
        </div>

        {/* Navigation Buttons */}
        <div className="mt-auto pt-6 border-t border-gray-200 flex flex-col sm:flex-row gap-3 sm:justify-end">
           <button type="submit" className="w-full sm:w-auto flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-500 to-indigo-600 text-white font-semibold rounded-lg shadow-md hover:from-blue-600 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2 transition-all duration-200 transform hover:scale-[1.02]">
             Next <ArrowRight size={18} />
           </button>
        </div>
      </form>

      {/* Instructions Panel - this can remain as it is specific to this section */}
      <div className="bg-white/80 backdrop-blur-sm p-6 rounded-xl shadow-lg mt-8 border border-yellow-200">
        <h2 className="text-xl font-bold text-gray-800 mb-4">Retrofit Survey Guide</h2>
        <p className="text-gray-600 mb-4">
          This survey collects key information about the property to help determine the most suitable retrofit measures.
          Complete all fields as accurately as possible to receive the best recommendations.
        </p>
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-r-lg">
          <p className="text-sm text-yellow-800">
            Tip: For photo fields, the camera function allows you to take high-quality images of key property features.
            These photos will be included in the final retrofit assessment report.
          </p>
        </div>
      </div>
      
      {/* Photo Gallery Modal */}
      {selectedGallery && (
        <PhotoGalleryModal
          isOpen={!!selectedGallery}
          onClose={handleCloseGallery}
          photos={
            // Check if it's a custom image field or boolean field photos
            (selectedGallery.startsWith('custom_') || selectedGallery.endsWith('_photos'))
              ? // Convert data URLs to PhotoData format for custom/boolean fields
                ((formData as any)[selectedGallery] || []).map((url: string, idx: number) => ({
                  id: `${selectedGallery}_${idx}`,
                  url: url,
                  dataUrl: url,
                  note: '',
                  isUploading: false
                }))
              : // Use built-in photo section for standard fields
                photos[selectedGallery]?.photos || []
          }
          sectionTitle={
            selectedGallery.startsWith('custom_')
              ? // Find the field label for custom image fields
                customFields?.find((f: FieldSchema) => f.id === selectedGallery)?.label || 'Photos'
              : selectedGallery.endsWith('_photos')
              ? // Find the field label for boolean field photos
                (() => {
                  const baseFieldId = selectedGallery.replace('_photos', '');
                  const boolField = customFields?.find((f: FieldSchema) => f.id === baseFieldId);
                  return boolField ? `${boolField.label} - Evidence Photos` : 'Evidence Photos';
                })()
              : selectedGallery
          }
          onDeletePhoto={(photoId: string) => {
            if (selectedGallery.startsWith('custom_') || selectedGallery.endsWith('_photos')) {
              // Handle custom image field or boolean field photos deletion
              const currentValues = (formData as any)[selectedGallery] || [];
              const photoIndex = parseInt(photoId.split('_').pop() || '0');
              const updatedValues = currentValues.filter((_: any, idx: number) => idx !== photoIndex);
              const event = {
                target: {
                  name: selectedGallery,
                  value: updatedValues
                }
              } as any;
              handleInputChange(event);
            } else {
              handleDeletePhoto(selectedGallery, photoId);
            }
          }}
          onUpdateNote={(photoId: string, note: string) => {
            // Note: Custom image fields and boolean photos don't support notes yet
            // Could be added later if needed
            if (!selectedGallery.startsWith('custom_') && !selectedGallery.endsWith('_photos')) {
              handleUpdatePhotoNote(selectedGallery, photoId, note);
            }
          }}
        />
      )}
      
      {/* Signature Modal for custom signature fields */}
      {showSignatureModal && currentSignatureField && signatureRefs.current[currentSignatureField] && (
        <SignatureModal
          isOpen={showSignatureModal}
          onClose={() => setShowSignatureModal(false)}
          onSave={() => {
            const ref = signatureRefs.current[currentSignatureField];
            if (ref?.current && !ref.current.isEmpty()) {
              const dataUrl = ref.current.toDataURL();
              const currentDate = new Date().toLocaleDateString('en-GB', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric'
              });
              
              console.log(`[Signature Save] Saving signature for field: ${currentSignatureField}`, {
                dataUrlLength: dataUrl.length,
                dataUrlPreview: dataUrl.substring(0, 50) + '...',
                date: currentDate
              });
              
              // Save signature
              const signatureEvent = {
                target: {
                  name: currentSignatureField,
                  value: dataUrl
                }
              } as any;
              handleInputChange(signatureEvent);
              
              // Save date
              const dateEvent = {
                target: {
                  name: currentSignatureField + 'Date',
                  value: currentDate
                }
              } as any;
              handleInputChange(dateEvent);
              
              console.log(`[Signature Save] After handleInputChange, formData:`, {
                signatureFieldId: currentSignatureField,
                hasSignature: !!(formData as any)[currentSignatureField],
                hasDate: !!(formData as any)[currentSignatureField + 'Date']
              });
            }
            setShowSignatureModal(false);
          }}
          onClear={() => {
            const ref = signatureRefs.current[currentSignatureField];
            ref?.current?.clear();
          }}
          onDelete={() => {
            const ref = signatureRefs.current[currentSignatureField];
            ref?.current?.clear();
            
            // Clear signature
            const signatureEvent = {
              target: {
                name: currentSignatureField,
                value: ''
              }
            } as any;
            handleInputChange(signatureEvent);
            
            // Clear date
            const dateEvent = {
              target: {
                name: currentSignatureField + 'Date',
                value: ''
              }
            } as any;
            handleInputChange(dateEvent);
            
            setShowSignatureModal(false);
          }}
          title={customFields.find(f => f.id === currentSignatureField)?.label || 'Sign Here'}
          signatureRef={signatureRefs.current[currentSignatureField]}
          hasExistingSignature={!!(formData as any)[currentSignatureField]}
        />
      )}
      
      {/* Hidden signature canvases for custom signature fields */}
      <div style={{ display: 'none' }}>
        {customFields.filter(f => f.type === 'signature').map(field => (
          <SignatureCanvas key={field.id} ref={signatureRefs.current[field.id]} />
        ))}
      </div>
    </div>
  );
};

export default RetrofitSurvey; 